import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Sparkles, Video, Clock, Euro, Mail, Check } from 'lucide-react';
import { api } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { Seo } from '@/client/components/Seo';
import { isLocale, type Locale } from '@/client/i18n';
import type { ConsultationTypePublic } from '@/client/types/public-consultations';

const CONSULTATION_EMAIL = 'academy@dajanarodriguez.com';

const TOPIC_KEYS_SK = [
  'Branding a budovanie značky',
  'Marketing a stratégia',
  'Sociálne siete',
  'AI v podnikaní',
  'Vývoj produktu',
  'Výroba a produkcia',
  'Mindset a sebahodnota',
  'Ako zvládať podnikanie a mamičkovstvo zároveň',
  'Čokoľvek iné, kde potrebuješ moju radu',
];

const TOPIC_KEYS_CS = [
  'Branding a budování značky',
  'Marketing a strategie',
  'Sociální sítě',
  'AI v podnikání',
  'Vývoj produktu',
  'Výroba a produkce',
  'Mindset a sebehodnota',
  'Jak zvládat podnikání a mateřství zároveň',
  'Cokoli jiného, kde potřebuješ mou radu',
];

const PROCESS_STEPS_SK = [
  {
    title: 'Vyber si termín a povedz čo riešiš',
    body: 'V kalendári vyber svoj termín a v krátkom formulári mi povedz, o čom budeme hovoriť. Čím konkrétnejšie, tým lepšie sa pripravím.',
  },
  {
    title: 'Schválim rezerváciu',
    body: 'Termín si pred konečným potvrdením preverím. Dostaneš email, keď je všetko v poriadku.',
  },
  {
    title: 'Zaplatíš faktúru',
    body: 'Pošlem ti faktúru na email. Po jej úhrade máš termín 100 % rezervovaný.',
  },
  {
    title: 'Pripravím sa konkrétne pre teba',
    body: 'Pred stretnutím si pozriem tvoju situáciu, web, IG. Žiadne generické rady — reálne riešenia pre tvoj prípad.',
  },
  {
    title: 'Stretneme sa online',
    body: 'Pošlem ti link na Google Meet. Sadneme si na hodinu a odídeš s konkrétnymi krokmi.',
  },
];

const PROCESS_STEPS_CS = [
  {
    title: 'Vyber si termín a řekni co řešíš',
    body: 'V kalendáři vyber svůj termín a v krátkém formuláři mi řekni, o čem budeme mluvit. Čím konkrétněji, tím líp se připravím.',
  },
  {
    title: 'Schválím rezervaci',
    body: 'Termín si před konečným potvrzením prověřím. Dostaneš e-mail, jakmile je vše v pořádku.',
  },
  {
    title: 'Zaplatíš fakturu',
    body: 'Pošlu ti fakturu na e-mail. Po její úhradě máš termín 100 % rezervovaný.',
  },
  {
    title: 'Připravím se konkrétně pro tebe',
    body: 'Před schůzkou si projdu tvou situaci, web, IG. Žádné generické rady — reálná řešení pro tvůj případ.',
  },
  {
    title: 'Sejdeme se online',
    body: 'Pošlu ti link na Google Meet. Sedneme si na hodinu a odejdeš s konkrétními kroky.',
  },
];

export function ConsultationsList() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';

  // Fetch the first available consultation product so CTAs route to its
  // booking page. If none exists, the buttons fall back to the contact page.
  const list = useQuery({
    queryKey: ['public', 'consultations'],
    queryFn: () => api<ConsultationTypePublic[]>('/api/v1/consultations'),
  });

  const firstSlug = list.data?.[0]?.slug;
  const bookHref = firstSlug ? `/${locale}/konzultacie/${firstSlug}` : `/${locale}/kontakt`;

  const topics = locale === 'cs' ? TOPIC_KEYS_CS : TOPIC_KEYS_SK;
  const steps = locale === 'cs' ? PROCESS_STEPS_CS : PROCESS_STEPS_SK;

  return (
    <div>
      <Seo
        title={t('seo.consultations_title')}
        description={t('seo.consultations_description')}
        locale={locale}
      />

      {/* HERO with photo */}
      <section className="relative">
        <div
          className="absolute top-10 -right-32 w-[28rem] h-[28rem] rounded-full pointer-events-none animate-float-orb"
          style={{
            background: 'radial-gradient(circle, rgba(193,167,226,0.40) 0%, transparent 65%)',
            filter: 'blur(40px)',
          }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-12">
          <div className="grid gap-10 lg:gap-16 lg:grid-cols-2 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-flex items-center gap-2 rounded-pill bg-white/70 backdrop-blur-md border border-glass-border-strong px-4 py-1.5 font-sans text-caption uppercase tracking-[0.18em] text-primary animate-fade-up shadow-glass-soft">
                <Sparkles size={12} />
                {locale === 'cs' ? 'Konzultace' : 'Konzultácia'}
              </span>
              <h1
                className="mt-5 font-display text-[clamp(2.25rem,6vw,4rem)] leading-[1.05] tracking-tight text-primary-dark animate-fade-up"
                style={{ animationDelay: '70ms' }}
              >
                {locale === 'cs' ? 'Hodina přímo s Dajanou.' : 'Hodina priamo s Dajanou.'}
              </h1>
              <p
                className="mt-5 font-display text-body-large text-text-secondary leading-relaxed animate-fade-up"
                style={{ animationDelay: '140ms' }}
              >
                {locale === 'cs'
                  ? 'Online setkání, kde se podíváš se mnou na to, co řešíš. Položíš mi konkrétní otázku — a odejdeš s konkrétní odpovědí.'
                  : 'Online stretnutie, kde sa pozrieš so mnou na to, čo riešiš. Položíš mi konkrétnu otázku — a odídeš s konkrétnou odpoveďou.'}
              </p>
              <a
                href="#booking"
                className="inline-block mt-8 animate-fade-up"
                style={{ animationDelay: '210ms' }}
              >
                <Button size="lg">
                  {locale === 'cs' ? 'Vyber si termín' : 'Vyber si termín'}
                  <ArrowRight size={18} />
                </Button>
              </a>
            </div>
            <div className="order-1 lg:order-2 animate-fade-up" style={{ animationDelay: '140ms' }}>
              <img
                src="/photos/DR_Academy_konzultacie_hero.jpg"
                alt={
                  locale === 'cs'
                    ? 'Dajana Varga Rodriguez, zakladatelka Dajana Rodriguez Academy'
                    : 'Dajana Varga Rodriguez, founderka Dajana Rodriguez Academy'
                }
                loading="eager"
                decoding="async"
                className="w-full h-auto rounded-2xl object-cover aspect-[4/5] shadow-glass"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TOPICS */}
      <section className="relative py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-5 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="dr-eyebrow">
              {locale === 'cs' ? 'Témata' : 'Témy'}
            </p>
            <h2 className="mt-4 font-display text-h2 sm:text-h1 text-primary-dark">
              {locale === 'cs' ? 'S čím ti můžu pomoct' : 'S čím ti môžem pomôcť'}
            </h2>
            <p className="mt-5 font-display text-body-large text-text-secondary leading-relaxed max-w-2xl mx-auto">
              {locale === 'cs'
                ? 'Konzultace funguje na čemkoli, kde potřebuješ mou radu. Nejčastěji však řešíme tyto oblasti:'
                : 'Konzultácia funguje na čomkoľvek, kde potrebuješ moju radu. Najčastejšie však riešime tieto oblasti:'}
            </p>
          </div>
          <ul className="mt-10 grid gap-3 sm:gap-4 sm:grid-cols-2">
            {topics.map((topic) => (
              <li
                key={topic}
                className="flex items-start gap-3 dr-card-static px-5 py-4"
              >
                <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-white">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="font-sans text-body text-text-primary leading-relaxed">
                  {topic}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="dr-eyebrow">{locale === 'cs' ? 'Proces' : 'Proces'}</p>
            <h2 className="mt-4 font-display text-h2 sm:text-h1 text-primary-dark">
              {locale === 'cs' ? 'Jak to probíhá' : 'Ako to prebieha'}
            </h2>
          </div>
          <ol className="mt-12 grid gap-5 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 stagger-children">
            {steps.map((step, i) => (
              <li key={step.title} className="dr-card-static p-7 sm:p-8">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white font-display text-h4 leading-none">
                  {i + 1}
                </div>
                <h3 className="mt-5 font-display text-h4 text-primary-dark">{step.title}</h3>
                <p className="mt-3 font-sans text-body text-text-secondary leading-relaxed">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PRICING BOX */}
      <section className="relative py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-6 lg:px-8">
          <div className="dr-card-static p-8 sm:p-12 text-center">
            <p className="dr-eyebrow">{locale === 'cs' ? 'Co je v ceně' : 'Čo je v cene'}</p>
            <h2 className="mt-4 font-display text-h2 text-primary-dark">
              {locale === 'cs' ? 'Konzultace 1:1' : 'Konzultácia 1:1'}
            </h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-3 max-w-2xl mx-auto">
              <PriceFact
                Icon={Euro}
                label={locale === 'cs' ? 'Cena' : 'Cena'}
                value={locale === 'cs' ? '150 € s DPH' : '150 € s DPH'}
              />
              <PriceFact
                Icon={Clock}
                label={locale === 'cs' ? 'Délka' : 'Dĺžka'}
                value={locale === 'cs' ? '60 minut' : '60 minút'}
              />
              <PriceFact
                Icon={Video}
                label={locale === 'cs' ? 'Formát' : 'Formát'}
                value="Online (Google Meet)"
              />
            </div>
            <ul className="mt-8 space-y-3 text-left max-w-lg mx-auto">
              {(locale === 'cs'
                ? [
                    'Příprava konkrétně pro tvůj případ',
                    'Hodina přímého rozhovoru se mnou',
                    'Konkrétní odpovědi a kroky pro tvou situaci',
                  ]
                : [
                    'Príprava konkrétne pre tvoj prípad',
                    'Hodina priameho rozhovoru so mnou',
                    'Konkrétne odpovede a kroky pre tvoju situáciu',
                  ]
              ).map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-white">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <span className="font-sans text-body text-text-primary leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
            <a href="#booking" className="inline-block mt-8">
              <Button size="lg">
                {locale === 'cs' ? 'Vyber si termín' : 'Vyber si termín'}
                <ArrowRight size={18} />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* BOOKING */}
      <section id="booking" className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-6 lg:px-8 text-center">
          <p className="dr-eyebrow">{locale === 'cs' ? 'Rezervace' : 'Rezervácia'}</p>
          <h2 className="mt-4 font-display text-h2 sm:text-h1 text-primary-dark">
            {locale === 'cs' ? 'Vyber si termín' : 'Vyber si termín'}
          </h2>
          <p className="mt-5 font-display text-body-large text-text-secondary leading-relaxed">
            {locale === 'cs'
              ? 'Vyber si volný termín a v krátkém formuláři mi řekni, co budeš řešit.'
              : 'Vyber si voľný termín a v krátkom formulári mi povedz, čo budeš riešiť.'}
          </p>
          <div className="mt-10">
            {list.isLoading ? (
              <div className="dr-card-static h-32 animate-shimmer" />
            ) : (
              <Link to={bookHref}>
                <Button size="lg">
                  {locale === 'cs' ? 'Otevřít rezervaci' : 'Otvoriť rezerváciu'}
                  <ArrowRight size={18} />
                </Button>
              </Link>
            )}
          </div>
          <p className="mt-12 font-sans text-body text-text-secondary">
            {locale === 'cs' ? 'Máš ještě pár otázek? Napiš mi: ' : 'Máš ešte pár otázok? Napíš mi: '}
            <a
              href={`mailto:${CONSULTATION_EMAIL}`}
              className="inline-flex items-center gap-1.5 text-primary hover:text-primary-dark underline underline-offset-4 decoration-primary/30 hover:decoration-primary"
            >
              <Mail size={14} />
              {CONSULTATION_EMAIL}
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}

function PriceFact({
  Icon,
  label,
  value,
}: {
  Icon: typeof Euro;
  label: string;
  value: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon size={18} />
      </div>
      <p className="font-sans text-caption uppercase tracking-[0.14em] text-text-muted">
        {label}
      </p>
      <p className="font-display text-h4 text-primary-dark text-center">{value}</p>
    </div>
  );
}
